import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getCharacterPortraitCandidates, SHURIKEN_PORTRAIT } from '../../../utils/characterPortrait'
import './MissionIssuerCard.css'

function metaLine(person) {
  if (!person || person.hidden) return null
  if (person.role === 'daimyo' && person.hanName) {
    return person.hanName
  }
  const parts = []
  if (person.clanName) parts.push(String(person.clanName).trim())
  if (person.rankName) parts.push(String(person.rankName).trim())
  if (person.extraCount > 0) parts.push(`+${person.extraCount}`)
  return parts.length ? parts.join(' · ') : null
}

function defaultKicker(person) {
  if (!person || person.hidden) return 'Anónimo'
  if (person.role === 'daimyo') return 'Daimyo'
  return 'Emisor'
}

function PersonBody({
  hidden,
  empty,
  slug,
  portrait,
  badge,
  displayName,
  meta,
  imgSrc,
  setImgSrc,
  portraitCandidates = [SHURIKEN_PORTRAIT],
}) {
  return (
    <>
      <div className="mic__portrait" aria-hidden="true">
        <div
          className={`mic__frame${
            !hidden && !empty && (slug || portrait) ? '' : ' mic__frame--seal'
          }`}
        >
          <img
            src={imgSrc}
            alt=""
            loading="lazy"
            onError={() => {
              const idx = portraitCandidates.indexOf(imgSrc)
              const next = portraitCandidates[idx + 1] || SHURIKEN_PORTRAIT
              if (next !== imgSrc) setImgSrc(next)
            }}
          />
        </div>
      </div>
      <div className="mic__copy">
        <span className="mic__kicker">{badge}</span>
        <span className="mic__name">{displayName}</span>
        {meta ? <span className="mic__meta">{meta}</span> : null}
      </div>
    </>
  )
}

/**
 * Mini-ficha de emisor/aceptador del tablón.
 * Con slug → abre ficha; sin slug + onCreateIssuerSheet → crea ficha.
 */
export default function MissionPersonCard({
  person = null,
  kicker = null,
  emptyLabel = 'Sin asignar',
  ariaRoleLabel = 'Persona',
  onCreateIssuerSheet = null,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const hidden = Boolean(person?.hidden)
  const empty = !person && !hidden
  const displayName = hidden ? 'Anónimo' : person?.name || emptyLabel
  const slug = !hidden && person?.slug ? String(person.slug).trim() : null
  const portrait = !hidden && person ? person.portrait || null : null
  const meta = empty ? null : metaLine(person)
  const badge = kicker || defaultKicker(person) || '—'
  const lightNpc = Boolean(person?.isMissionIssuer || person?.lightNpcSheet)
  const sheetTo = slug
    ? lightNpc
      ? `/personajes/${slug}/npc`
      : `/personajes/${slug}/ficha`
    : null
  const canOpenSheet = Boolean(sheetTo)
  const canCreateSheet = Boolean(
    !slug && !hidden && !empty && typeof onCreateIssuerSheet === 'function'
  )

  const portraitCandidates = !hidden && !empty
    ? getCharacterPortraitCandidates({
        portrait,
        slug,
        name: person?.name,
        preferSm: true,
      })
    : [SHURIKEN_PORTRAIT]
  const [imgSrc, setImgSrc] = useState(portraitCandidates[0] || SHURIKEN_PORTRAIT)

  useEffect(() => {
    const next = !hidden && !empty
      ? getCharacterPortraitCandidates({
          portrait,
          slug,
          name: person?.name,
          preferSm: true,
        })
      : [SHURIKEN_PORTRAIT]
    setImgSrc(next[0] || SHURIKEN_PORTRAIT)
  }, [empty, hidden, portrait, slug, person?.name])

  const bodyProps = {
    hidden,
    empty,
    slug,
    portrait,
    badge,
    displayName,
    meta,
    imgSrc,
    setImgSrc,
    portraitCandidates,
  }

  if (canOpenSheet) {
    const from = `${location.pathname}${location.search || ''}`
    return (
      <Link
        to={sheetTo}
        state={{ from }}
        className="mic mic--link"
        title={`Ver ficha de ${displayName}`}
        aria-label={`${ariaRoleLabel}: ${displayName}. Abrir ficha`}
        onClick={(e) => {
          e.preventDefault()
          navigate(sheetTo, { state: { from } })
        }}
      >
        <PersonBody {...bodyProps} />
      </Link>
    )
  }

  if (canCreateSheet) {
    return (
      <button
        type="button"
        className="mic mic--link mic--create-sheet"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onCreateIssuerSheet()
        }}
        title={`Crear ficha de ${displayName}`}
        aria-label={`${ariaRoleLabel}: ${displayName}. Crear ficha`}
      >
        <PersonBody {...bodyProps} />
      </button>
    )
  }

  return (
    <div className="mic" aria-label={`${ariaRoleLabel}: ${displayName}`}>
      <PersonBody {...bodyProps} />
    </div>
  )
}

/** Compat: emisor del tablón */
export function MissionIssuerCard({ issuer = null, onCreateIssuerSheet = null }) {
  return (
    <MissionPersonCard
      person={issuer}
      kicker={null}
      emptyLabel="Sin asignar"
      ariaRoleLabel="Emisor"
      onCreateIssuerSheet={onCreateIssuerSheet}
    />
  )
}
